import { Button } from "../ui/button";
import { useEffect, useState } from "react";
import { getAllReviews } from "../../services/api";
import { useNavigate } from "react-router-dom";

type Review = {
  id: string;
  client_name: string;
  comment: string;
  media_url: string;
};

const Reviews: React.FC = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const displayCount = 6; // Number of reviews to show on homepage

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await getAllReviews() as any;
      setReviews(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleViewAllReviews = () => {
    navigate("/reviews");
  };

  if (loading) {
    return (
      <section className="py-12 md:py-16 lg:py-24 bg-white">
        <div className="lg:max-w-6xl mx-auto px-4">
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#b05d5d]"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error && reviews.length === 0) {
    return (
      <section className="py-12 md:py-16 lg:py-24 bg-white">
        <div className="lg:max-w-6xl mx-auto px-4">
          <div className="text-center text-red-500">{error}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-6 pb-12 md:pt-8 md:pb-16 lg:pt-12 lg:pb-24 bg-white">
      <div className="lg:max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="h-[30px] w-[202px] lg:h-[72px] lg:w-[202px] flex items-center justify-center mx-auto mb-4">
            <img src="/images/reviewText.svg" alt="Reviews" />
          </div>
          <p className="text-gray-600 text-base md:text-lg">
            Real experiences from our beautiful clients
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {reviews.slice(0, displayCount).map((review, index) => (
            <div
              key={`${review.id}-${index}`}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
            >
              {/* Image section hidden for homepage */}
              {/* <div className="relative aspect-video bg-gray-100 overflow-hidden group">
                <img
                  src={review.media_url}
                  alt={`${review.client_name}'s review`}
                  className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                {[".mp4", ".mov", ".webm", ".avi"].some((ext) =>
                  review.media_url?.toLowerCase().endsWith(ext)
                ) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src="/images/videoplayer.svg"
                      alt="Play"
                      className="w-12 h-12 md:w-16 md:h-16"
                    />
                  </div>
                )}
              </div> */}

              <div className="p-4 md:p-5 lg:p-6">
                <p className="text-gray-800 mb-3 md:mb-4 italic text-sm md:text-base line-clamp-3">
                  {review.comment}
                </p>
                <p className="text-right text-rose-800 text-sm md:text-base font-medium">
                  ~ {review.client_name}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* View All Reviews Button */}
        <div className="text-center mt-8 md:mt-12">
          <Button
            onClick={handleViewAllReviews}
            className="bg-[#B87775] text-white hover:bg-[#a16967] border-none rounded-md px-8 py-4 lg:px-10 lg:py-6 text-base lg:text-lg font-medium"
          >
            VIEW ALL REVIEWS
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
